import React from 'react';
import { Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type { CostType, E2IConfig, Quotation } from '../../types/e2i';
import type { AdjustMode } from '../../utils/e2i';
import { cleanDateInput, costTypeName, formatAmount, formatDate, formatPct } from '../../utils/e2i';
import InfoField from './InfoField';
import { QuoteDraft, repriceDraft } from './quotationDraft';

interface Props {
  idPrefix: string;
  config: E2IConfig;
  currency: string;
  quotation: Quotation;
  draft: QuoteDraft;
  onChange: (next: QuoteDraft) => void;
  editable: boolean;
}

const ADJUST_OPTIONS: Array<{ value: AdjustMode; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'pct', label: 'Percentage' },
  { value: 'amount', label: 'Amount' },
];

/** The quotation's pricing and terms: markups per cost type, profit, extra charge, discount, VAT, validity, scope. */
const QuotationPricing: React.FC<Props> = ({ idPrefix, config, currency, quotation: q, draft, onChange, editable }) => {
  const costTypes: CostType[] = config.costTypes;
  const used = Array.from(new Set((editable ? draft.lines : q.lines || []).map((l) => l.costType || '').filter(Boolean)));
  const set = <K extends keyof QuoteDraft>(key: K, value: QuoteDraft[K]) => onChange({ ...draft, [key]: value });
  /** A markup or the profit changed: the cost lines are priced again. */
  const setPricing = (patch: Partial<QuoteDraft>) => onChange(repriceDraft({ ...draft, ...patch }, costTypes));
  const markupText = (code: string) => draft.markups[code] ?? String(costTypes.find((c) => c.code === code)?.markup ?? 0);
  const vatOptions = config.vatCodes.length || !draft.vatCode ? config.vatCodes : [{ code: draft.vatCode, name: draft.vatCode, rate: q.vatRate }];

  if (!editable) {
    return (
      <Grid container spacing={2}>
        {used.length ? (
          <Grid item xs={12} md={6}>
            <InfoField
              label="Markups by cost type"
              value={used.map((code) => `${costTypeName(code, costTypes)} ${formatPct(q.markups?.[code] ?? costTypes.find((c) => c.code === code)?.markup ?? 0)}`).join(' · ')}
            />
          </Grid>
        ) : null}
        <Grid item xs={6} md={3}><InfoField label="Profit" value={formatPct(q.profitPct)} /></Grid>
        <Grid item xs={6} md={3}><InfoField label="VAT code" value={q.vatCode ? `${q.vatCode} (${formatPct(q.vatRate)})` : ''} /></Grid>
        <Grid item xs={12} sm={6} md={3}>
          <InfoField
            label="Extra charge"
            value={q.addChargePct ? formatPct(q.addChargePct) : q.addChargeAmount ? `${formatAmount(q.addChargeAmount)} ${currency}` : ''}
            hint={q.addChargeReason || undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <InfoField label="Discount" value={q.discountPct ? formatPct(q.discountPct) : q.discountAmount ? `${formatAmount(q.discountAmount)} ${currency}` : ''} />
        </Grid>
        <Grid item xs={6} md={3}><InfoField label="Valid until" value={formatDate(q.validUntil)} /></Grid>
        <Grid item xs={6} md={3}><InfoField label="Client reference" value={q.clientRef} /></Grid>
        <Grid item xs={12} md={6}><InfoField label="Scope of work" value={q.scope} multiline /></Grid>
        <Grid item xs={12} md={6}><InfoField label="Terms" value={q.terms} multiline /></Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      {used.length ? (
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
            Markups by cost type (the company defaults until changed): the cost lines are priced again as you type
          </Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {used.map((code) => (
              <TextField
                key={code}
                id={`${idPrefix}-markup-${code}`}
                label={`${costTypeName(code, costTypes)} markup %`}
                value={markupText(code)}
                onChange={(e) => setPricing({ markups: { ...draft.markups, [code]: e.target.value } })}
                inputProps={{ inputMode: 'decimal' }}
                sx={{ width: { xs: '100%', sm: 200 } }}
              />
            ))}
          </Stack>
        </Grid>
      ) : null}
      <Grid item xs={12} sm={6} md={3}>
        <TextField id={`${idPrefix}-profit`} label="Profit %" value={draft.profit} onChange={(e) => setPricing({ profit: e.target.value })} inputProps={{ inputMode: 'decimal' }} fullWidth helperText="On top of the markups, on every cost line" />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField id={`${idPrefix}-vat`} select label="VAT code" value={draft.vatCode} onChange={(e) => set('vatCode', e.target.value)} fullWidth SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
          <MenuItem value=""><em>None</em></MenuItem>
          {vatOptions.map((v) => <MenuItem key={v.code} value={v.code}>{v.code} · {v.name} ({formatPct(v.rate)})</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          id={`${idPrefix}-valid`}
          type="date"
          label="Valid until"
          value={draft.validUntil}
          onChange={(e) => set('validUntil', cleanDateInput(e.target.value))}
          InputLabelProps={{ shrink: true }}
          fullWidth
          helperText={`Company default: ${config.quoteValidityDays} days`}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField id={`${idPrefix}-clientref`} label="Client reference" value={draft.clientRef} onChange={(e) => set('clientRef', e.target.value.slice(0, 100))} fullWidth helperText="The client's enquiry or tender number" />
      </Grid>
      <Grid item xs={12} sm={4} md={2}>
        <TextField id={`${idPrefix}-charge-mode`} select label="Extra charge" value={draft.chargeMode} onChange={(e) => set('chargeMode', e.target.value as AdjustMode)} fullWidth>
          {ADJUST_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
      </Grid>
      {draft.chargeMode !== 'none' ? (
        <>
          <Grid item xs={12} sm={3} md={2}>
            <TextField id={`${idPrefix}-charge`} label={draft.chargeMode === 'pct' ? 'Charge %' : `Charge ${currency}`.trim()} value={draft.chargeValue} onChange={(e) => set('chargeValue', e.target.value)} inputProps={{ inputMode: 'decimal' }} fullWidth required />
          </Grid>
          <Grid item xs={12} sm={5} md={4}>
            <TextField id={`${idPrefix}-charge-reason`} label="Reason for the charge" value={draft.chargeReason} onChange={(e) => set('chargeReason', e.target.value.slice(0, 254))} fullWidth required />
          </Grid>
        </>
      ) : null}
      <Grid item xs={12} sm={4} md={2}>
        <TextField id={`${idPrefix}-discount-mode`} select label="Discount" value={draft.discountMode} onChange={(e) => set('discountMode', e.target.value as AdjustMode)} fullWidth>
          {ADJUST_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
      </Grid>
      {draft.discountMode !== 'none' ? (
        <Grid item xs={12} sm={3} md={2}>
          <TextField id={`${idPrefix}-discount`} label={draft.discountMode === 'pct' ? 'Discount %' : `Discount ${currency}`.trim()} value={draft.discountValue} onChange={(e) => set('discountValue', e.target.value)} inputProps={{ inputMode: 'decimal' }} fullWidth required />
        </Grid>
      ) : null}
      <Grid item xs={12} md={6}>
        <TextField id={`${idPrefix}-scope`} label="Scope of work (printed)" value={draft.scope} onChange={(e) => set('scope', e.target.value.slice(0, 8000))} multiline minRows={3} fullWidth helperText="Empty: the enquiry's subject" />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField id={`${idPrefix}-terms`} label="Terms (printed)" value={draft.terms} onChange={(e) => set('terms', e.target.value.slice(0, 8000))} multiline minRows={3} fullWidth />
      </Grid>
    </Grid>
  );
};

export default QuotationPricing;
